import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Chip } from "@mui/material";
import MainApi from "../../../../../../services/mainApi.service";
import { memo, useCallback, useMemo, useState } from "react";
import { OverflowTooltip, Table } from "@4uhub/lib4uhub";
import { Preferences } from "./PreferencesForm/Preferences";
import { ITicketAutomationReceivedRulePreferences } from "../../../../../../models/ticket-aut-rec-rule-preference";

const TICKET_AUTOMATION_RECEIVED_RULE_PREFERENCE_ROUTE =
  process.env.REACT_APP_AUTOMATIONS +
  "/api/v1/TicketAutomationReceivedRulePreference/";
const ticketAutomationReceivedRulePreferenceService =
  new MainApi<ITicketAutomationReceivedRulePreferences>(
    TICKET_AUTOMATION_RECEIVED_RULE_PREFERENCE_ROUTE,
  );

const translationPath =
  "chatBot.page.automations.automationReceivedRules.ticketAutomationReceivedRulePreference.";

const PreferencesPage = () => {
  const { t } = useTranslation();

  const { id: ticketAutomationReceivedRuleId } = useParams();

  const [preferenceForm, setPreferenceForm] = useState(false);

  const [selectedId, setSelectedId] = useState<string>();

  const columns: GridColDef<ITicketAutomationReceivedRulePreferences>[] = [
    {
      field: "medicalSpecialtyExternalName",
      headerName:
        t(translationPath + "medical_specialty") || "Medical Specialty",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketAutomationRecRulePrefPrefs",
      headerName: t(translationPath + "calendar_preferences") || "Preferences",
      renderCell: ({ row: { ticketAutomationRecRulePrefPrefs } }) => {
        if (
          !ticketAutomationRecRulePrefPrefs ||
          ticketAutomationRecRulePrefPrefs.length === 0
        )
          return "-";
        const preferences = ticketAutomationRecRulePrefPrefs
          .map((preference) => preference.externalName.trim())
          .join("; ");
        return (
          <OverflowTooltip
            text={preferences}
            tooltip={preferences}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      renderCell: ({ row: { enable } }) => {
        return (
          <Chip
            label={
              enable
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={enable ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    setPreferenceForm(true);
  };

  const handleRowClick = (
    params: GridRowParams<ITicketAutomationReceivedRulePreferences>,
  ) => {
    if (!params.row) return;
    setPreferenceForm(true);
    setSelectedId(params.row.id);
  };

  const handleBack = useCallback(() => {
    setSelectedId(undefined);
    setPreferenceForm(false);
  }, []);

  const props = useMemo(() => {
    return {
      TicketAutomationReceivedRuleId: ticketAutomationReceivedRuleId,
    };
  }, [ticketAutomationReceivedRuleId]);

  return (
    <Box sx={{ height: 600, width: "100%" }}>
      {preferenceForm && (
        <Box
          sx={(t) => ({
            p: 1,
            px: 10,
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: t.palette.background.default,
            borderRadius: 1,
          })}
        >
          <Preferences
            id={selectedId}
            ticketAutomationReceivedRuleId={ticketAutomationReceivedRuleId}
            onBackButton={handleBack}
          />
        </Box>
      )}
      {!preferenceForm && (
        <Box height={600}>
          <Table<ITicketAutomationReceivedRulePreferences>
            showDefaultMacroFilters={false}
            service={ticketAutomationReceivedRulePreferenceService}
            columns={columns}
            title={""}
            onAdd={handleAdd}
            onRowClick={handleRowClick}
            searchInputAutoFocus={false}
            defaultPageSize={20}
            serviceProps={props}
            pageSizeOptions={[5, 10, 20]}
            searchInputPlaceHolder={""}
            addButtonLabel={
              t(translationPath + "add_preference") || "Add Preference"
            }
            confirmOptions={{
              title: t(translationPath + "confirm.title"),
              description: t(translationPath + "confirm.description"),
              confirmationButtonProps: {
                variant: "contained",
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default memo(PreferencesPage);
